// Redemption-rate unit reconciliation for the Points tab.
//
// The wire field `LoyaltyRules.redemption.rate` is stored as DOLLARS-PER-POINT
// (the dollar value of a single point, e.g. `0.05` → 1 pt = $0.05). The UI — and
// the design mockup's `#redemptionRate` field — presents POINTS-PER-DOLLAR
// (e.g. `20` → 20 pts = $1), which is the reciprocal. These helpers convert at
// the Points Formik boundary only; the shared rules gateway and `utils/format.ts`
// are intentionally left untouched (see plan: "Points form only").
//
// Both directions are the same `1/x` transform with float-noise normalization so
// clean rates round-trip exactly (0.05 ↔ 20, 0.01 ↔ 100).

import { REDEMPTION_RATE_FALLBACK } from '../../../utils/format'

const toPositiveNumber = (
  value: string | number | null | undefined,
): number => {
  const n = typeof value === 'string' ? Number.parseFloat(value) : value
  return Number.isFinite(n) && (n as number) > 0 ? (n as number) : Number.NaN
}

// Strip binary float noise (1/0.05 → 19.999999999999996 → 20) while preserving
// legitimate fractional rates.
const normalize = (n: number): string => String(Number(n.toPrecision(12)))

// Wire (dollars-per-point) → display (points-per-dollar). Falls back to the
// mockup default (20 pts = $1) when the stored value is missing or invalid.
export const pointsPerDollarFromWireRate = (
  wire: string | number | null | undefined,
): string => {
  const n = toPositiveNumber(wire)
  if (Number.isNaN(n)) return String(REDEMPTION_RATE_FALLBACK)
  return normalize(1 / n)
}

// Display (points-per-dollar) → wire (dollars-per-point). Returns '0' for invalid
// input; the Yup `positive` guard blocks this from ever reaching the PUT body.
export const wireRateFromPointsPerDollar = (
  ppd: string | number | null | undefined,
): string => {
  const n = toPositiveNumber(ppd)
  if (Number.isNaN(n)) return '0'
  return normalize(1 / n)
}
